import {
  resolveBookedNights,
  resolveStayCheckInDisplay,
  resolveStayCheckOutDisplay,
} from '../../../utils/bookingStayDisplay';
import { formatHotelDate } from '../../../utils/dateUtils';
import { getBookingCurrencySymbol, getPaymentSummaryData } from '../../../utils/bookingPaymentSummary';
import {
  ARRIVAL_MODE_OPTIONS,
  isForeignGuestRecord,
  resolveCountryName,
} from '../../../utils/foreignGuestFormUtils';
import { formatDocumentType, formatPolicyTime, getSelectedHotelFromContext, joinNonEmpty } from './grcUtils';

const GRC_SETTLEMENT_OPTIONS = [
  { key: 'CASH', label: 'Cash' },
  { key: 'CARD', label: 'Card' },
  { key: 'UPI', label: 'UPI' },
  { key: 'ONLINE', label: 'Online' },
  { key: 'BANK TRANSFER', label: 'Bank Transfer' },
  { key: 'CHEQUE', label: 'Cheque' },
  { key: 'CREDIT SALE', label: 'Credit Sale' },
  { key: 'PAYMENT LINK', label: 'Payment Link' },
  { key: 'COMPANY SETTLEMENT', label: 'Company' },
  { key: 'OTHERS', label: 'Others' },
];

const GRC_KNOWN_SETTLEMENT = new Set(
  GRC_SETTLEMENT_OPTIONS.filter((option) => option.key !== 'OTHERS').map((option) => option.key),
);

const PURPOSE_LABELS = {
  leisure: 'Leisure',
  holiday: 'Holiday',
  business: 'Business',
  personal: 'Personal',
  medical: 'Medical',
  pilgrimage: 'Pilgrimage',
  education: 'Education',
  conference: 'Conference',
  other: 'Other',
};

function text(value) {
  if (value == null) return '';
  return String(value).trim();
}

function formatMoney(amount, currency = 'INR', { showZero = false } = {}) {
  if (amount == null || amount === '') return showZero ? formatMoney(0, currency, { showZero: true }) : '';
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) return '';
  if (numeric === 0 && !showZero) return '';
  const symbol = getBookingCurrencySymbol(currency);
  return `${symbol}${numeric.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatGender(value) {
  const raw = text(value).toLowerCase();
  if (!raw) return '';
  if (raw === 'm' || raw === 'male') return 'Male';
  if (raw === 'f' || raw === 'female') return 'Female';
  if (raw === 'o' || raw === 'other') return 'Other';
  return text(value).replace(/^\w/, (ch) => ch.toUpperCase());
}

function formatGuestName(guest, fallbackName) {
  const titled = joinNonEmpty([
    guest?.title,
    guest?.first_name,
    guest?.middle_name,
    guest?.last_name,
  ], ' ');
  return titled || text(guest?.name) || text(fallbackName);
}

function formatPurpose(booking) {
  const display = text(booking?.purpose_of_stay_display);
  if (display && display !== '—') return display;
  const code = text(booking?.purpose_of_stay).toLowerCase();
  const label = PURPOSE_LABELS[code] || text(booking?.purpose_of_stay);
  const note = text(booking?.purpose_of_stay_note);
  if (label && note) return `${label} — ${note}`;
  return label;
}


function normalizePaymentMethod(value) {
  return text(value).toUpperCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
}

function inferSettlementMethods(booking) {
  const selected = new Set();
  const payments = Array.isArray(booking?.payments) ? booking.payments : [];
  payments.forEach((payment) => {
    const raw = normalizePaymentMethod(payment?.payment_method || payment?.method || payment?.mode);
    if (raw) selected.add(raw);
  });
  const summaryMethod = normalizePaymentMethod(
    booking?.payment_summary?.primary_method
      || booking?.payment_method
      || booking?.settlement_method,
  );
  if (summaryMethod) selected.add(summaryMethod);

  const othersChecked = [...selected].some((method) => (
    method === 'CASH DEPOSIT' || !GRC_KNOWN_SETTLEMENT.has(method)
  ));

  return GRC_SETTLEMENT_OPTIONS.map((option) => ({
    key: option.key,
    label: option.label,
    checked: option.key === 'OTHERS' ? othersChecked : selected.has(option.key),
  }));
}

function findAdvancePayment(booking) {
  const payments = Array.isArray(booking?.payments) ? booking.payments : [];
  return payments.find((payment) => {
    const kind = text(payment?.payment_type || payment?.kind || payment?.category).toLowerCase();
    return kind.includes('advance');
  }) || payments[0] || null;
}

function resolveRoomContext(booking, roomNumber) {
  const items = booking?.items || booking?.booking_items || [];
  const wanted = text(roomNumber);
  const matching = wanted
    ? items.filter((item) => {
        const number = text(item?.allocated_room?.number || item?.allocated_room_number);
        return number && number === wanted;
      })
    : items;

  const sourceItems = matching.length ? matching : items;
  const numbers = [...new Set(
    sourceItems
      .map((item) => text(item?.allocated_room?.number || item?.allocated_room_number))
      .filter(Boolean),
  )];

  const rentTotal = sourceItems.reduce((sum, item) => sum + (Number(item?.price) || 0), 0);
  const nights = new Set(
    sourceItems.map((item) => text(item?.date).slice(0, 10)).filter(Boolean),
  ).size;
  const adults = Math.max(0, ...sourceItems.map((item) => Number(item?.adults) || 0));
  const children = Math.max(0, ...sourceItems.map((item) => Number(item?.children) || 0));
  const extraAdults = Math.max(0, ...sourceItems.map((item) => Number(item?.extra_adults) || 0));
  const extraChildren = Math.max(0, ...sourceItems.map((item) => Number(item?.extra_children) || 0));
  const extraCount = extraAdults + extraChildren;

  const roomType = text(
    sourceItems[0]?.room_type?.name
      || sourceItems[0]?.roomType?.name
      || booking?.room_type_name,
  );

  return {
    roomNumber: wanted || numbers.join(', '),
    roomType,
    roomRent: nights > 1 ? rentTotal / nights : rentTotal,
    guestCount: adults + children
      || (Number(booking?.pax_adults) || 0) + (Number(booking?.pax_children) || 0)
      || '',
    extraBed: extraCount > 0 ? String(extraCount) : '',
  };
}

function formatStayDateTime(display, dateFallback) {
  if (display?.full) return display.full;
  if (display?.date && display?.time) return `${display.date}, ${display.time}`;
  if (display?.date) return display.date;
  return dateFallback ? formatHotelDate(dateFallback) : '';
}

export function buildGuestRegistrationCardData({
  documents,
  hotel = getSelectedHotelFromContext(),
  branding = {},
} = {}) {
  const guest = documents?.guest || {};
  const booking = documents?.booking || {};
  const currency = booking.currency || hotel.currency || 'INR';
  const paymentSummary = getPaymentSummaryData(booking, booking.payments || []);
  const advancePayment = findAdvancePayment(booking);
  const room = resolveRoomContext(booking, documents?.roomNumber);
  const hotelCountry = hotel.country || booking.hotel?.country || '';
  const isForeign = isForeignGuestRecord(guest, hotelCountry);
  const identityType = formatDocumentType(documents?.document_type || guest.document_type);
  const identityNumber = text(documents?.id_number || guest.id_number);
  const address = joinNonEmpty([guest.address_line1, guest.address_line2]);
  const arrival = resolveStayCheckInDisplay(booking) || {};
  const departure = resolveStayCheckOutDisplay(booking) || {};
  const arrivalMode = text(guest.arrival_mode);
  const arrivalModeLabel = ARRIVAL_MODE_OPTIONS.find((option) => option.value === arrivalMode)?.label
    || text(guest.arrival_mode);

  const hotelAddress = joinNonEmpty([
    branding.address || hotel.address,
    branding.city || hotel.city,
    hotel.pincode,
    resolveCountryName(hotel.country) || hotel.country,
  ]);

  return {
    hotelName: text(branding.hotelName || hotel.name) || 'Hotel',
    hotelTagline: text(branding.tagline),
    hotelLogoUrl: text(branding.logoUrl || hotel.logo_url),
    hotelGstin: text(branding.gstin || hotel.gstin),
    hotelAddress,
    hotelEmail: text(branding.email || hotel.contact_email || hotel.email),
    hotelPhone: text(branding.phone || hotel.phone_number || hotel.phone),
    hotelWebsite: text(branding.website || hotel.website),
    checkInPolicy: formatPolicyTime(
      branding.checkInTime || hotel.check_in_time || booking.hotel_check_in_time,
      '02:00 PM',
    ),
    checkOutPolicy: formatPolicyTime(
      branding.checkOutTime || hotel.check_out_time || booking.hotel_check_out_time,
      '11:00 AM',
    ),
    grCardNo: text(documents?.bookingCode || booking.code),
    billNo: text(booking.invoice_number || booking.bill_no),
    guestName: formatGuestName(guest, documents?.guestName),
    phone: text(guest.phone || documents?.guestPhone),
    address,
    city: text(guest.city),
    country: resolveCountryName(guest.country || guest.nationality) || text(guest.country),
    identity: joinNonEmpty([identityType, identityNumber], '  ·  '),
    arrivalDateTime: formatStayDateTime(arrival, booking.check_in_date),
    departureDateTime: formatStayDateTime(departure, booking.check_out_date),
    checkInDate: text(arrival.date) || (booking.check_in_date ? formatHotelDate(booking.check_in_date) : ''),
    checkInTime: text(arrival.time),
    checkOutDate: text(departure.date) || (booking.check_out_date ? formatHotelDate(booking.check_out_date) : ''),
    checkOutTime: text(departure.time),
    comingFrom: text(guest.arrival_from),
    nextDestination: '',
    nationality: resolveCountryName(guest.nationality || guest.country) || text(guest.nationality),
    purposeOfVisit: formatPurpose(booking),
    dateOfBirth: guest.dob ? formatHotelDate(guest.dob) : '',
    gender: formatGender(guest.gender),
    email: text(guest.email),
    isForeign,
    passportNumber: text(guest.passport_number),
    passportIssuePlace: text(guest.passport_issue_place),
    passportIssueDate: guest.passport_issue_date ? formatHotelDate(guest.passport_issue_date) : '',
    arrivedInCountryOn: guest.arrived_in_country_on ? formatHotelDate(guest.arrived_in_country_on) : '',
    dateOfStayInIndia: booking.check_in_date && isForeign ? formatHotelDate(booking.check_in_date) : '',
    modeOfTransport: arrivalModeLabel,
    vehicleNo: text(guest.arrival_transport_number),
    driverName: '',
    settlementMethods: inferSettlementMethods(booking),
    creditCardNo: '',
    bankName: text(booking.company_name || guest.company_name),
    roomNo: room.roomNumber,
    roomType: room.roomType,
    nights: resolveBookedNights(booking) || '',
    stayDates: joinNonEmpty([
      booking.check_in_date ? formatHotelDate(booking.check_in_date) : '',
      booking.check_out_date ? formatHotelDate(booking.check_out_date) : '',
    ], ' – '),
    persons: room.guestCount ? String(room.guestCount) : '',
    roomRent: formatMoney(room.roomRent, currency),
    noOfGuest: room.guestCount ? String(room.guestCount) : '',
    extraBed: room.extraBed,
    bookingCharges: formatMoney(paymentSummary?.displaySubtotal ?? paymentSummary?.subtotalBeforeDiscount, currency, { showZero: true }),
    discountAmount: formatMoney(paymentSummary?.discountAmount, currency),
    extraCharges: formatMoney(paymentSummary?.extraChargesAmount, currency),
    totalTax: formatMoney(paymentSummary?.displayGstAmount ?? paymentSummary?.gstAmount, currency, { showZero: true }),
    totalAmount: formatMoney(paymentSummary?.totalAmount, currency, { showZero: true }),
    amountPaid: formatMoney(paymentSummary?.paidAmount ?? booking.advance_amount, currency, { showZero: true }),
    balance: formatMoney(paymentSummary?.remainingAmount, currency, { showZero: true }),
    advanceDeposit: formatMoney(paymentSummary?.paidAmount || booking.advance_amount, currency),
    receiptNo: text(
      advancePayment?.receipt_number
        || advancePayment?.receipt_no
        || advancePayment?.reference
        || advancePayment?.transaction_id,
    ),
    receiptDate: advancePayment?.processed_at
      ? formatHotelDate(advancePayment.processed_at)
      : '',
    formC: isForeign ? 'Required' : '',
    printedOn: '',
  };
}
